import type { BookingState } from '../state/types';

/**
 * Is there ANY sign that the guest actually progressed through the booking
 * flow? Standard bookings carry `selectedDate`/`selectedTime`; both are null on
 * a cold landing (deep link, bookmark, shared URL, search-engine hit).
 *
 * The non-standard flows reach late steps WITHOUT those fields — waiting-list
 * bookings use `wlDates` + `flowType`, early-access and agent bookings are
 * flag-driven — so keying on date/time alone would misread a real in-progress
 * guest as a cold landing. Every flag here defaults to a benign value in
 * `DEFAULT_BOOKING_STATE` ('standard' / false / []), so a true cold load still
 * reads as session-less.
 *
 * Deliberately NOT `startedAt`: BookingProvider stamps it on every mount, so it
 * is set even on a pure cold load and cannot discriminate.
 *
 * Extracted from `bookingFinalGuard` so the /booking-final guard and the
 * inactive-step recovery share ONE definition of "has a session" — two copies
 * would drift, and both decide whether to eject a guest from the flow.
 */
export function hasActiveBookingSession(state: BookingState): boolean {
  return Boolean(
    state.selectedDate ||
      state.selectedTime ||
      state.flowType === 'waiting_list' ||
      state.wlDates.length > 0 ||
      state.isEarlyAccessFlow ||
      state.isAgentReservation,
  );
}
